/**
 * Google Workspace OIDC, Authorization Code + PKCE — frontend provider.
 *
 * @see .grizzly/kb/sop-auth-provider-architecture.md
 *
 * Differences from the GitHub provider, all deliberate:
 *
 *   - PKCE. The browser cannot hold a client secret, so the code verifier is
 *     what binds the authorization code to this browser.
 *   - Strict state check. The GitHub provider accepts a callback when the saved
 *     state has gone missing; that is the CSRF defence disabling itself exactly
 *     when it is needed. Here a missing or mismatched state fails the login.
 *   - No console logging of the exchange. Codes, state, and redirect URIs are
 *     credentials or near-credentials and do not belong in a browser console.
 */

import type { AuthProvider } from '../types.js'
import { reportUnhandledError } from '../../otel.js'
import { oauthRedirectUri } from '../redirectUri.js'
import { createPkcePair, PKCE_KEYS, randomUrlSafe } from '../pkce.js'

const TOKEN_KEY = 'grizzly_auth_token'
const STATE_KEY = 'grizzly_auth_state'
const VERIFIER_KEY = PKCE_KEYS.login
const REDIRECT_KEY = 'grizzly_auth_redirect'
const GOOGLE_AUTHORIZE_URL = 'https://accounts.google.com/o/oauth2/v2/auth'

const CLIENT_ID = process.env.GOOGLE_CLIENT_ID ?? ''
/**
 * Sent as the `hd` authorization parameter so the account chooser only offers
 * work accounts. It is a UI hint ONLY — the binding check is the `hd` claim on
 * the ID token, verified server-side. Never treat this as a security control.
 */
const WORKSPACE_DOMAIN = process.env.GOOGLE_WORKSPACE_DOMAIN ?? ''

export class GoogleAuthProvider implements AuthProvider {
  readonly name = 'google'

  initiateLogin(): void {
    if (!CLIENT_ID) {
      reportUnhandledError(new Error('GOOGLE_CLIENT_ID not configured'), 'auth.google.initiate')
      return
    }
    void this.startAuthorization()
  }

  private async startAuthorization(): Promise<void> {
    try {
      const state = randomUrlSafe(16)
      const { verifier, challenge } = await createPkcePair()
      const redirectUri = oauthRedirectUri()

      sessionStorage.setItem(STATE_KEY, state)
      sessionStorage.setItem(VERIFIER_KEY, verifier)
      sessionStorage.setItem(REDIRECT_KEY, redirectUri)

      const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        // No refresh token is requested: nothing here needs offline access, and
        // a refresh token would need storage and rotation.
        prompt: 'select_account',
      })
      if (WORKSPACE_DOMAIN) params.set('hd', WORKSPACE_DOMAIN)

      location.href = `${GOOGLE_AUTHORIZE_URL}?${params.toString()}`
    } catch (error) {
      reportUnhandledError(
        error instanceof Error ? error : new Error(String(error)),
        'auth.google.initiate',
      )
    }
  }

  async handleCallback(): Promise<boolean> {
    const url = new URL(location.href)
    const code = url.searchParams.get('code')
    const state = url.searchParams.get('state')
    const error = url.searchParams.get('error')

    const savedState = sessionStorage.getItem(STATE_KEY)
    const verifier = sessionStorage.getItem(VERIFIER_KEY)
    const redirectUri = sessionStorage.getItem(REDIRECT_KEY) ?? oauthRedirectUri()

    // Clear first, so a replayed callback cannot reuse them.
    sessionStorage.removeItem(STATE_KEY)
    sessionStorage.removeItem(VERIFIER_KEY)
    sessionStorage.removeItem(REDIRECT_KEY)

    if (error) {
      reportUnhandledError(new Error(`Google auth error: ${error}`), 'auth.google.callback')
      return false
    }
    if (!code) return false

    // Fail closed. A missing saved state means this browser did not start the
    // flow, which is exactly the case CSRF protection exists for.
    if (!state || !savedState || state !== savedState) {
      reportUnhandledError(new Error('OAuth state missing or mismatched'), 'auth.google.state')
      return false
    }
    if (!verifier) {
      reportUnhandledError(new Error('PKCE verifier missing'), 'auth.google.state')
      return false
    }

    try {
      const res = await fetch('/api/auth/google/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri, code_verifier: verifier }),
      })
      if (!res.ok) {
        // Status only. The body can echo request detail back into the console.
        reportUnhandledError(new Error(`exchange failed: ${res.status}`), 'auth.google.exchange')
        return false
      }
      const json = (await res.json()) as {
        data?: { token: string }
        error?: { message: string }
      }
      if (json.error || !json.data?.token) {
        reportUnhandledError(
          new Error(json.error?.message ?? 'no token in exchange response'),
          'auth.google.exchange',
        )
        return false
      }
      sessionStorage.setItem(TOKEN_KEY, json.data.token)
      return true
    } catch (err) {
      reportUnhandledError(
        err instanceof Error ? err : new Error(String(err)),
        'auth.google.exchange',
      )
      return false
    }
  }

  getToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY)
  }

  logout(): void {
    sessionStorage.removeItem(TOKEN_KEY)
  }

  /**
   * Is there a credential that is still worth sending?
   *
   * "Is the string non-empty" is not the same question, and answering only that
   * is what left the app in limbo: a Google ID token lives about an hour, so
   * anyone who came back to an open tab the next morning passed the boot gate
   * on a dead token, then watched every panel fail with "Invalid or expired
   * token" and no way to reach the login page.
   *
   * This is an OPTIMISATION, not the security control — the token is verified
   * server-side on every call, and handleUnauthorized() recovers from a 401
   * whatever the client believed. It exists so the common case (expired while
   * away) shows a login page immediately instead of a wall of failed panels.
   */
  isAuthenticated(): boolean {
    const t = sessionStorage.getItem(TOKEN_KEY)
    if (typeof t !== 'string' || t.length === 0) return false
    const expiresAt = idTokenExpiry(t)
    // Unreadable `exp` → let the server decide. Failing closed here would sign
    // out anyone holding a token shape we did not anticipate, and the 401 path
    // already covers being wrong in this direction.
    if (expiresAt === null) return true
    // 30s of slack absorbs clock skew and the flight time of a request that is
    // about to be sent — a token expiring mid-request is the same limbo.
    return Date.now() < expiresAt - 30_000
  }
}

/**
 * `exp` (ms) from a JWT, or null when it cannot be read.
 *
 * Deliberately does NOT verify the signature: this is a "should I bother
 * sending it" check, and a browser cannot verify a Google signature anyway. The
 * backend verifies properly on every request (server/api/auth/providers/google.ts).
 */
function idTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    // base64url → base64, then pad. atob rejects both differences.
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')
    const claims = JSON.parse(atob(padded)) as { exp?: unknown }
    return typeof claims.exp === 'number' ? claims.exp * 1000 : null
  } catch {
    return null
  }
}
